import { CommonModule, DatePipe, DecimalPipe } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from "@angular/common/http";
import {
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { NgModule, enableProdMode, ErrorHandler, SecurityContext } from "@angular/core";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { AppComponent } from "./app.component";
import { PermissionRedirectPlaceholderComponent } from "./_guards/permissionRedirect.guard";
import { AppRoutingModule } from "./app-routing.module";
import { ChartService } from "./services/chart.service";
import { CommonComponentsModule } from "./common-components/common-components.module";
import { CustomToastComponent } from "./shared/custom-toast/custom-toast.component";
import { DIALOG_R_DATA } from "./injector/dialog-data.token";
import { DashboardFooterComponent } from "./components/dashboard-footer/dashboard-footer.component";
import { DataAnalysisPageComponent } from "./components/dataAnalysis/dataAnalysisPage.component";
import { DateFormatService } from "./services/dateFormat.service";
import { DeviceDetailComponent } from "./components/device-detail/device-detail.component";
import { DeviceVersionListComponent } from "./components/deviceVersionList/deviceVersionList.component";
import { DialogModalsModule } from "./modals/dialog-modals.module";
import { DialogService } from "./services/dialog.service";
import { EndpointInfoComponent } from "./components/endpoint-info/endpoint-info.component";
import { EndpointsComponent } from "./components/endpoints/endpoints.component";
import { EndpointsTableComponent } from "./components/endpoints-table/endpoints-table.component";
import { FooterComponent } from "./components/footer/footer.component";
import { HomeComponent } from "./components/home/home.component";
import { LoaderService } from "./services/loader.service";
import { MainComponent } from "./components/main/main.component";
import { MsalModule } from "@azure/msal-angular";
import { NodeDetailComponent } from "./components/node-detail/node-detail.component";
import { NotificationListComponent } from "./components/notification-list/notification-list.component";
import { NotificationSettingsComponent } from "./components/notification-settings/notification-settings.component";
import { OverlayService } from "./services/overlay.service";
import { PageHeaderComponent } from "./components/pageHeader/pageHeader.component";
import { PaginationInfoComponent } from "./components/pagination-info/pagination-info.component";
import { PreSignupUserListComponent } from "./components/preSignup/preSignupUserList.component";
import { RealTimeMonitoringDashboardComponent } from "./components/real-time-monitoring-dashboard/real-time-monitoring-dashboard.component";
import { ReportBodyComponent } from "./components/reports/report-body/report-body.component";
import { ReportHeaderComponent } from "./components/reports/report-header/report-header.component";
import { ReportListComponent } from "./components/report-list/report-list.component";
import { ReportViewComponent } from "./components/report-view/report-view.component";
import { ReportsComponent } from "./components/reports/reports.component";
import { ReportChartComponent } from "./components/reports/report-chart/report-chart.component";
import { RouterOutlet } from "@angular/router";
import { SampleListComponent } from "./components/sample-list/sample-list.component";
import { SegListComponent } from "./components/seg-list/seg-list.component";
import { SensorComponent } from "./components/sensor/sensor.component";
import { ShadowConstraintListComponent } from "./components/shadowConstraintList/shadowConstraintList.component";
import { SharedModule } from "./shared/shared.module";
import { SidebarComponent } from "./components/sidebar/sidebar.component";
import { SignupComponent } from "./components/signup/signup.component";
import { TenantListComponent } from "./components/tenantList/tenantList.component";
import { TenantDialogComponent } from "./modals/tenant-dialog/tenant-dialog.component";
import { TenantSensorListComponent } from "./components/tenantSensorList/tenantSensorList.component";
import { TimeZoneService } from "./services/timeZone.service";
import { ToastService } from "./services/toast.service";
import { ToastrModule } from "ngx-toastr";
import { CacheInterceptor, TokenInterceptor } from "./_helpers";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { UserListComponent } from "./components/user-list/user-list.component";
import { UserAccountSecurityPopupComponent } from "./components/user-list/user-account-security-popup/user-account-security-popup.component";
import { WorkforceComponent } from "./components/workforce/workforce.component";
import { WorkforceTableComponent } from "./components/workforce-table/workforce-table.component";
import { ZonesComponent } from "./components/zones/zones.component";
import { environment } from "src/environments/environment";
import { ResetPasswordComponent } from "./components/reset-password/reset-password.component";
import { InfoWindowComponent } from "./components/info-window/info-window.component";
import { ForgotPasswordComponent } from "./components/forgot-password/forgot-password.component";
import { LogoComponent } from "./components/logo/logo.component";
import { LoginComponent } from "./components/login/login.component";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { DateTimeAdapter, OWL_DATE_TIME_FORMATS, OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { DeviceDetailsMapClustererComponent } from "./shared/map/device-details-map-clusterer/device-details-map-clusterer.component";
import { LocationListComponent } from "./components/location-list/location-list.component";
import { PpeListComponent } from "./components/ppe-list/ppe-list.component";
import { ControlsListComponent } from "./components/controls-list/controls-list.component";
import { TaskListComponent } from "./components/task-list/task-list.component";
import { CustomOwlDateAdapter } from "./services/custom-owl-date-adapter";
import { AlertResponseComponent } from "./components/alert-response/alert-response.component";
import { AlertResponsePreviewComponent } from "./components/alert-response-preview/alert-response-preview.component";
import { RolePermissionListComponent } from "./components/role-permission-list/role-permission-list.component";
import { RoleRailComponent } from "./components/role-rail/role-rail.component";
import { RolePermissionsTabComponent } from "./components/role-permissions-tab/role-permissions-tab.component";
import { RolePermissionMatrixComponent } from "./components/role-permission-matrix/role-permission-matrix.component";
import { RolePermissionSaveBarComponent } from "./components/role-permission-save-bar/role-permission-save-bar.component";
import { RolePermissionSaveResultBannerComponent } from "./components/role-permission-save-result-banner/role-permission-save-result-banner.component";
import { RiskAssessmentComponent } from "./components/risk-assessment/risk-assessment.component";
import { RiskRegisterComponent } from './components/risk-register/risk-register.component';
import { RiskRegisterMatrixCellComponent } from './components/risk-register/risk-register-matrix-cell/risk-register-matrix-cell.component';
import { RiskRegisterCardSlotPickerComponent } from './modals/risk-register-card-slot-picker/risk-register-card-slot-picker.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AgentsListComponent } from './components/agents-list/agents-list.component';
import { JurisdictionsListComponent } from './components/jurisdictions-list/jurisdictions-list.component';
import { DevicesTableComponent } from "./components/devices-table/devices-table.component";
import { NodeDetailsMapClustererComponent } from "./shared/map/node-details-map-clusterer/node-details-map-clusterer.component";
import { LanguageSwitcherComponent } from './components/language-switcher/language-switcher.component';
import { SurveyListComponent } from './components/survey-list/survey-list.component';
import { AgentGroupListComponent } from "./components/agent-group-list/agent-group-list.component";
import { CalibrationEquipmentComponent } from './components/calibration-equipment/calibration-equipment.component';
import { SamplingEquipmentComponent } from './components/sampling-equipment/sampling-equipment.component';
import { SampleMediaComponent } from './components/sample-media/sample-media.component';
import { AnalyticalMethodComponent } from './components/analytical-method/analytical-method.component';
import { OrganizationsComponent } from './components/organizations/organizations.component';
import { GridSettingsFlyoutComponent } from "./shared/grid/core-grid/grid-settings-flyout/grid-settings-flyout.component";
import { TableViewSelectorComponent } from "./shared/grid/core-grid/table-view-selector/table-view-selector.component";
import { ExportWizardDialogComponent } from "./shared/grid/core-grid/export-wizard-dialog/export-wizard-dialog.component";
import { SampleHistoryComponent } from './components/sample-history/sample-history.component';
import { FilterTypeComponent } from "./shared/grid/core-grid/grid-settings-flyout/filter-type/filter-type.component";
import { SearchInputComponent } from "./shared/grid/core-grid/grid-settings-flyout/search-input/search-input.component";
import { OelTypeComponent } from './components/oel-type/oel-type.component';
import { JobDictionaryComponent } from './components/job-dictionary/job-dictionary.component';
import { JobDictionaryConfigDialogComponent } from './modals/job-dictionary-config-dialog/job-dictionary-config-dialog.component';
import { JobDictionaryDialogComponent } from './modals/job-dictionary-dialog/job-dictionary-dialog.component';
import { JobDictionaryHistoryDialogComponent } from './modals/job-dictionary-history-dialog/job-dictionary-history-dialog.component';
import { JobMappingComponent } from './components/job-mapping/job-mapping.component';
import { LabIntegrationsComponent } from './components/lab-integrations/lab-integrations.component';
import { LabRequisitionListComponent } from './components/lab-requisition-list/lab-requisition-list.component';
import { FileAttachmentListComponent } from './components/file-attachment-list/file-attachment-list.component';
import { AccessLogListComponent } from './components/access-log-list/access-log-list.component';
import { ParametersListComponent } from './components/parameters-list/parameters-list.component';
import { NodeAnalyticsChartComponent } from './components/node-detail/node-analytics-chart/node-analytics-chart.component';
import { NodeSyncPanelComponent } from './components/node-detail/node-sync-panel/node-sync-panel.component';
import { CompareTabComponent } from './components/node-detail/compare-tab/compare-tab.component';
import * as Sentry from '@sentry/angular';
import { LabIntegrationDetailsComponent } from './components/lab-integration-details/lab-integration-details.component';
import { ShiftListComponent } from './components/shift-list/shift-list.component';
import { SampleTemplateListComponent } from './components/sample-template-list/sample-template-list.component';
import { SurveyTemplateListComponent } from './components/survey-template-list/survey-template-list.component';
import { TemplateKindToggleComponent } from './components/template-kind-toggle/template-kind-toggle.component';
import { TemplateListHostComponent } from './components/template-list-host/template-list-host.component';
import { SamplePlanningListComponent } from './components/sample-planning-list/sample-planning-list.component';
import { SamplePlanningDetailComponent } from './components/sample-planning-detail/sample-planning-detail.component';
import { ReportTemplateListComponent } from './components/report-template-list/report-template-list.component';
import { ListFilterToolbarComponent } from './shared/components/list-filter-toolbar/list-filter-toolbar.component';
import { MarkdownModule, SANITIZE } from 'ngx-markdown';
import { AiAnalystComponent } from './components/ai-analyst/ai-analyst.component';
import { CorporateReportingComponent } from './components/corporate-reporting/corporate-reporting.component';
import { ChatHistoryRailComponent } from './components/ai-analyst/chat-history-rail/chat-history-rail.component';
import { ChatEmptyStateComponent } from './components/ai-analyst/chat-empty-state/chat-empty-state.component';
import { ChatThreadComponent } from './components/ai-analyst/chat-thread/chat-thread.component';
import { ChatComposerComponent } from './components/ai-analyst/chat-composer/chat-composer.component';
import { CreditChipComponent } from './components/ai-analyst/credit-chip/credit-chip.component';
import { ConsumptionPopoverComponent } from './components/ai-analyst/consumption-popover/consumption-popover.component';
import { AnswerRendererComponent } from './components/ai-analyst/answer-renderer/answer-renderer.component';
import { ChatTurnErrorComponent } from './components/ai-analyst/chat-turn-error/chat-turn-error.component';
import { AiAnalystAdminComponent } from './components/ai-analyst-admin/ai-analyst-admin.component';
import { AnalystEnablementComponent } from './components/ai-analyst-admin/analyst-enablement/analyst-enablement.component';
import { SiteCreditBalanceListComponent } from './components/site-credit-balance-list/site-credit-balance-list.component';
import { AiUsageByUserListComponent } from './components/ai-usage-by-user/ai-usage-by-user-list.component';
import { AiUsageByUserToolbarComponent } from './components/ai-usage-by-user/ai-usage-by-user-toolbar/ai-usage-by-user-toolbar.component';
import { AiMonthStepperComponent } from './components/ai-analyst-admin/ai-month-stepper/ai-month-stepper.component';
import { CreditsPerDayChartComponent } from './components/ai-analyst-admin/credits-per-day-chart/credits-per-day-chart.component';
import { ChatService } from './services/chat.service';
import { ChatHttpService } from './services/chat-http.service';
import { AnalystOutputComponent } from './components/ai-analyst/analyst-output/analyst-output.component';
import { AnalystTableComponent } from './components/ai-analyst/analyst-table/analyst-table.component';
import { AnalystChartComponent } from './components/ai-analyst/analyst-chart/analyst-chart.component';
import { AnalystSourcesComponent } from './components/ai-analyst/analyst-sources/analyst-sources.component';
import { CitationOverflowComponent } from './components/ai-analyst/citation-overflow/citation-overflow.component';
import { CitationPanelComponent } from './components/ai-analyst/citation-panel/citation-panel.component';
import { AnalystClarificationComponent } from './components/ai-analyst/analyst-clarification/analyst-clarification.component';
import { AiAnalystSuccessMetricsComponent } from './components/ai-analyst-admin/ai-analyst-success-metrics/ai-analyst-success-metrics.component';
import { AnalystNarrowingOptionsComponent } from './components/ai-analyst/analyst-narrowing-options/analyst-narrowing-options.component';
import { AngularResizeEventModule } from 'angular-resize-event-package';

export function dateFormatFactory(dateFormatService: DateFormatService) {
  const { userDateFormat, userTimeFormat, userDateWithTimeFormat } = dateFormatService; // Default format
  return {
    parseInput: 'l LT',
    fullPickerInput: userDateWithTimeFormat,
    datePickerInput: userDateFormat,
    timePickerInput: userTimeFormat,
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
}

if (environment.production) {
  enableProdMode();
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export function loggerCallback(logLevel: LogLevel, message: string) {
}

@NgModule({
  declarations: [
    AppComponent,
    PermissionRedirectPlaceholderComponent,
    EndpointsTableComponent,
    ZonesComponent,
    UserListComponent,
    WorkforceTableComponent,
    WorkforceComponent,
    SegListComponent,
    SampleListComponent,
    ReportsComponent,
    ReportViewComponent,
    ReportListComponent,
    ReportChartComponent,
    RealTimeMonitoringDashboardComponent,
    PaginationInfoComponent,
    NotificationListComponent,
    NotificationSettingsComponent,
    NodeDetailComponent,
    MainComponent,
    HomeComponent,
    EndpointsComponent,
    EndpointInfoComponent,
    DevicesTableComponent,
    DeviceDetailComponent,
    DashboardFooterComponent,
    ReportBodyComponent,
    ReportHeaderComponent,
    FooterComponent,
    TenantSensorListComponent,
    ShadowConstraintListComponent,
    DeviceVersionListComponent,
    SidebarComponent,
    PageHeaderComponent,
    PreSignupUserListComponent,
    DataAnalysisPageComponent,
    SensorComponent,
    SignupComponent,
    ResetPasswordComponent,
    InfoWindowComponent,
    ForgotPasswordComponent,
    TenantListComponent,
    LogoComponent,
    LoginComponent,
    DeviceDetailsMapClustererComponent,
    NodeDetailsMapClustererComponent,
    CustomToastComponent,
    LocationListComponent,
    PpeListComponent,
    TaskListComponent,
    RolePermissionListComponent,
    RiskAssessmentComponent,
    RiskRegisterComponent,
    AgentsListComponent,
    AgentGroupListComponent,
    JurisdictionsListComponent,
    LanguageSwitcherComponent,
    SurveyListComponent,
    CalibrationEquipmentComponent,
    SamplingEquipmentComponent,
    SampleMediaComponent,
    AnalyticalMethodComponent,
    OrganizationsComponent,
    GridSettingsFlyoutComponent,
    ExportWizardDialogComponent,
    TableViewSelectorComponent,
    SampleHistoryComponent,
    FilterTypeComponent,
    SearchInputComponent,
    OelTypeComponent,
    LabIntegrationsComponent,
    LabRequisitionListComponent,
    FileAttachmentListComponent,
    ControlsListComponent,
    LabIntegrationDetailsComponent,
    ShiftListComponent,
    ParametersListComponent,
    SampleTemplateListComponent,
    SamplePlanningListComponent,
    SamplePlanningDetailComponent,
    NodeAnalyticsChartComponent,
    NodeSyncPanelComponent,
    CompareTabComponent,
    ReportTemplateListComponent,
    AiAnalystComponent,
    ChatHistoryRailComponent,
    ChatEmptyStateComponent,
    ChatThreadComponent,
    ChatComposerComponent,
    CreditChipComponent,
    AnswerRendererComponent,
    AnalystOutputComponent,
    AnalystTableComponent,
    AnalystChartComponent,
    AnalystSourcesComponent,
    CitationPanelComponent,
    ChatTurnErrorComponent,
    ConsumptionPopoverComponent,
    AiAnalystAdminComponent,
    AnalystEnablementComponent,
    SiteCreditBalanceListComponent,
    AnalystClarificationComponent,
    AiAnalystSuccessMetricsComponent,
    CorporateReportingComponent,
    AccessLogListComponent,
    JobDictionaryComponent,
    JobDictionaryConfigDialogComponent,
    JobDictionaryDialogComponent,
    JobDictionaryHistoryDialogComponent,
    JobMappingComponent,
    TenantDialogComponent,
    UserAccountSecurityPopupComponent,
    AnalystNarrowingOptionsComponent,
    AlertResponseComponent,
    AlertResponsePreviewComponent,
    RoleRailComponent,
    RolePermissionsTabComponent,
    RolePermissionMatrixComponent,
    RolePermissionSaveBarComponent,
    RolePermissionSaveResultBannerComponent,
    CitationOverflowComponent,
    AiUsageByUserListComponent,
    AiUsageByUserToolbarComponent,
    AiMonthStepperComponent,
    CreditsPerDayChartComponent,
    RiskRegisterMatrixCellComponent,
    RiskRegisterCardSlotPickerComponent,
    TemplateListHostComponent,
    TemplateKindToggleComponent,
    SurveyTemplateListComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    DialogModalsModule,
    CommonComponentsModule,
    RouterOutlet,
    AppRoutingModule,
    HttpClientModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    ToastrModule.forRoot({
      toastComponent: CustomToastComponent,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MsalModule.forRoot(
      new PublicClientApplication({
        auth: {
          clientId: environment.microsoft.client_id, // Application (client) ID from the app registration
          authority: environment.microsoft.authority, // The Azure cloud instance and the app's sign-in audience (tenant ID, common, organizations, or consumers)
          redirectUri: environment.microsoft.redirectUri, // This is your redirect URI
        },
        cache: {
          cacheLocation: "localStorage",
        },
        system: {
          loggerOptions: {
            loggerCallback,
            logLevel: LogLevel.Verbose,
            piiLoggingEnabled: false,
          },
          // MSAL v5 starts this timer when the popup opens; the default 60s expires during a
          // slow sign-in (MFA number match, account picker, first-time consent), so give the
          // user 5 minutes to finish before the BroadcastChannel wait is abandoned.
          popupBridgeTimeout: 300000,
        },
      }),
      {
        interactionType: InteractionType.Popup,
        authRequest: {
          scopes: ["user.read"],
        },
      },
      {
        interactionType: InteractionType.Popup,
        protectedResourceMap: new Map([
          ["https://graph.microsoft.com/v1.0/me", ["user.read"]],
        ]),
      }
    ),
    ListFilterToolbarComponent,
    MarkdownModule.forRoot({ sanitize: { provide: SANITIZE, useValue: SecurityContext.HTML } }),
    AngularResizeEventModule,
    ScrollingModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    LoaderService,
    [DatePipe],
    [DecimalPipe],
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true,
    },
    DateFormatService,
    ToastService,
    OverlayService,
    TimeZoneService,
    ChartService,
    {
      provide: DIALOG_R_DATA,
      useValue: [],
    },
    DialogService,
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: dateFormatFactory,
      deps: [DateFormatService]
    },
    {
      provide: DateTimeAdapter,
      useClass: CustomOwlDateAdapter
    },
    {
      provide: ChatService,
      useClass: ChatHttpService
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
